import React, { useContext } from 'react';
import styled from 'styled-components';
import CartCrossSells from '@components/cart/CartCrossSells';
import Discount from '@components/cart/Discount';
import GiftCardCartItemRow from '@components/cart/GiftCardCartItemRow';
import OneOffPurchaseCartItemRow from '@components/cart/OneOffPurchaseCartItemRow';
import ShippingCartItemRow from '@components/cart/ShippingCartItemRow';
import SubscriptionCartItemRow from '@components/cart/SubscriptionCartItemRow';
import { Context } from '@components/Context';
import { CartItem, CartItemType, GiftCardCartItem, ProductCode, SubscriptionCartItem } from '@contracts';
import { calculatePaidCartItemsCount } from '@utils/cart';
import CreditCartItemRow from './CreditCartItemRow';

interface Props {
  checkout: boolean;
  textColor: string;
  orderSuccessPage?: boolean;
}

const CartContents: React.FC<Props> = ({ checkout, textColor, orderSuccessPage = false }) => {
  const { state } = useContext(Context);
  const { cartItems, discountAmount, currency, shippingCost } = state;
  const subscriptions = cartItems
    .filter((cartItem) => cartItem.type === CartItemType.Subscription)
    .sort((a, b) => b.productCode.localeCompare(a.productCode));
  const oneOffPurchases = cartItems.filter(
    (cartItem) => cartItem.type === CartItemType.OneOffPurchase || cartItem.type === CartItemType.Consultation
  );
  const freeOneOffs = oneOffPurchases.filter((cartItem) => cartItem.price === 0);
  const paidOneOffs = oneOffPurchases.filter((cartItem) => cartItem.price > 0);
  const giftCards = cartItems.filter((cartItem) => cartItem.type === CartItemType.GiftCard);
  const paidCartItemsCount = calculatePaidCartItemsCount(cartItems);
  const hasSubscriptions = subscriptions.length > 0;
  const hasOneOffPurchases = oneOffPurchases.length > 0;
  const hasGiftCards = giftCards.length > 0;
  const discountValue = discountAmount ?? 0;
  const cartSectionClassName = `cart-section ${checkout && 'is-checkout'}`;
  const maxItemsToShow = freeOneOffs.filter((cartItem) => cartItem.productCode === ProductCode.TamponTin).length > 0 ? 2 : 3;
  const digitalProducts = [ProductCode.GiftCard, ProductCode.PPCStandard, ProductCode.PPCAdvanced, ProductCode.PPCPremium];
  const onlyDigital = cartItems.every((cartItem) => digitalProducts.includes(cartItem.productCode));
  const showCredit = !orderSuccessPage && checkout;

  return (
    <CartWrapper>
      {hasSubscriptions && (
        <CartSection className={cartSectionClassName}>
          {subscriptions.map((subscription: CartItem) => (
            <SubscriptionCartItemRow
              key={`${subscription.productCode}-${subscription.id}-${subscription.type}`}
              item={subscription as SubscriptionCartItem}
              editable={!checkout}
              subscriptionPlans={(subscription as SubscriptionCartItem).subscriptionPlans}
            />
          ))}
        </CartSection>
      )}
      {hasOneOffPurchases && (
        <CartSection className={cartSectionClassName}>
          {freeOneOffs.map((oneOffPurchase: any) => (
            <OneOffPurchaseCartItemRow
              key={oneOffPurchase.id}
              item={oneOffPurchase}
              editable={oneOffPurchase.editable && !checkout}
              textColor={textColor}
            />
          ))}
          {paidOneOffs.map((oneOffPurchase: any) => (
            <OneOffPurchaseCartItemRow
              key={oneOffPurchase.id}
              item={oneOffPurchase}
              editable={oneOffPurchase.editable && !checkout}
              textColor={textColor}
            />
          ))}
        </CartSection>
      )}
      {hasGiftCards && (
        <CartSection key="gift-cards-section" className={cartSectionClassName}>
          {giftCards.map((giftCard) => (
            <GiftCardCartItemRow key={giftCard.id} giftCard={giftCard as GiftCardCartItem} />
          ))}
        </CartSection>
      )}
      {!onlyDigital && (
        <CartSection className={cartSectionClassName}>
          <ShippingCartItemRow
            originalShippingPrice={shippingCost.originalShippingPrice}
            shippingPrice={shippingCost.shippingPrice}
            currency={currency}
            paidCartItemsCount={paidCartItemsCount}
            textColor={textColor}
          />
        </CartSection>
      )}
      {discountValue > 0 && (
        <CartSection className={cartSectionClassName}>
          <Discount discountValue={discountValue} currency={currency} showDiscountValue={orderSuccessPage} />
        </CartSection>
      )}
      {showCredit ? (
        <CartSection className={cartSectionClassName}>
          <CreditCartItemRow />
        </CartSection>
      ) : null}
      {!checkout && <CartCrossSells maxItemsToShow={maxItemsToShow} />}
    </CartWrapper>
  );
};

export default CartContents;

const CartWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 30px;
  padding: 30px 0;
  width: 100%;
`;

const CartSection = styled.div`
  padding-right: 60px;
  width: 100%;

  &.is-checkout {
    padding-right: 0;

    .cart-item-remove {
      display: none;
    }

    .shipping-price,
    .credit-price {
      margin-right: 0;
    }
  }

  @media only screen and (max-width: 768px) {
    padding: 0 30px;

    &.is-checkout {
      padding: 0 30px;
    }
  }

  &:empty {
    display: none;
  }
`;
